import React, { useState } from 'react';
import { Loader2, Mail, Lock, KeyRound, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaMessage, setMfaMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    const r = await login(email.trim().toLowerCase(), password, mfaCode.trim());
    setLoading(false);
    if (r.ok) {
      toast.success('¡Bienvenido!');
      return;
    }
    if (r.mfa_required) {
      setMfaRequired(true);
      setMfaMessage(r.message || 'Revisa tu correo o Telegram e ingresa el código de 6 dígitos.');
      toast.info('Código enviado por correo y Telegram');
      return;
    }
    toast.error(r.error || 'Error al iniciar sesión');
  };

  return (
    <div className="min-h-screen bg-[#0B1623] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background gradient accents */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-orange-500/30 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-amber-500/20 rounded-full blur-[100px]"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-br from-orange-500/20 to-amber-600/20 border border-orange-500/40 rounded-full px-4 py-1.5 mb-4">
            <Shield className="w-4 h-4 text-orange-400" />
            <span className="text-xs font-medium text-orange-300 uppercase tracking-wider">Acceso Seguro</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-1">
            HEBEH <span className="text-orange-400">Business</span>
          </h1>
          <p className="text-sm text-slate-400">Sistema Interno de Operaciones</p>
        </div>

        {/* Login Card */}
        <form onSubmit={onSubmit}
          className="bg-slate-900/80 backdrop-blur-sm border border-slate-700/60 rounded-2xl p-8 shadow-2xl space-y-5">
          
          {!mfaRequired ? (
            <>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5 font-medium uppercase tracking-wider">Correo</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    autoFocus
                    className="w-full pl-10 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none transition-colors"
                    placeholder="tu@correo.com"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1.5 font-medium uppercase tracking-wider">Contraseña</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="w-full pl-10 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none transition-colors"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200">
                📧 {mfaMessage}
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5 font-medium uppercase tracking-wider">Código de 6 dígitos</label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    required
                    autoFocus
                    className="w-full pl-10 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-lg tracking-widest font-mono text-center focus:border-orange-500 focus:outline-none transition-colors"
                    placeholder="000000"
                    data-testid="login-mfa-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1.5">El código expira en 10 minutos. Este dispositivo se recordará por 30 días.</p>
              </div>
              <button
                type="button"
                onClick={() => { setMfaRequired(false); setMfaCode(''); }}
                className="text-xs text-slate-400 hover:text-slate-200 underline">
                ← Usar otra cuenta
              </button>
            </>
          )}

          <button
            type="submit"
            disabled={loading || !email || !password || (mfaRequired && mfaCode.length !== 6)}
            className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-semibold rounded-lg transition-all shadow-lg shadow-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="login-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {mfaRequired ? 'Verificar código' : 'Iniciar sesión'}
          </button>
        </form>

        <p className="text-center text-xs text-slate-500 mt-6">
          © {new Date().getFullYear()} HEBEH EBENEZER S.A. DE C.V.
        </p>
      </div>
    </div>
  );
}
